import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { downloadActiveDataset, downloadAutoReviewReportByCalculationId, downloadEmptyTemplate, downloadReports } from '../../../api/GlobalActions/GlobalActions.api';
import { currDateAndTime, getExtension, saveStream, saveStreamCSV } from '../../../core/Utils/utils';
import { showErrorSnackbar, showSuccessSnackbar } from '../../snackbar/Snackbar.slice';
import { ProcessId } from '../../../core/Types/ProcessId.types';
import { AutoReviewProcessId } from '../../../core/Types/AutoReviewProcessId.types';
import { Messages } from '../../../Constants/Messages';
import { AppDispatch, RootState } from '../../../app/store';

export interface GlobalActionsState {
	activeDownloadRequests: number;
}

const initialState: GlobalActionsState = {
	activeDownloadRequests: 0,
};

export const downloadTemplate = createAsyncThunk<void, void, { state: RootState; dispatch: AppDispatch }>(
	'globalActions/downloadEmptyTemplate',
	async (arg, thunkAPI) => {
		const state = thunkAPI.getState();
		const response = await downloadEmptyTemplate(state.cem.selectedJob.id);
		const fileName = 'TRaDE Import Template' + getExtension(response.ContentType ?? 'application/octet-stream');
		if (response.Success) {
			saveStreamCSV(fileName, response.Data);
			showSuccessSnackbar(thunkAPI.dispatch, Messages.DATA_TEMPLATE_DOWNLOAD_SUCCESS);
		} else {
			showErrorSnackbar(thunkAPI.dispatch, Messages.DATA_TEMPLATE_DOWNLOAD_FAILED);
		}
	}
);

export const downloadPopulatedTemplate = createAsyncThunk<void, ProcessId, { state: RootState; dispatch: AppDispatch }>(
	'globalActions/downloadActiveDataset',
	async (processId, thunkAPI) => {
		const response = await downloadActiveDataset(processId.jobId, processId.datasetId);
		const fileName = currDateAndTime() + '_populated_dataset';
		if (response.Success) {
			saveStream(response.ContentType as string, fileName, response.Data);
			showSuccessSnackbar(thunkAPI.dispatch, Messages.DATA_TEMPLATE_DOWNLOAD_SUCCESS);
		} else {
			showErrorSnackbar(thunkAPI.dispatch, Messages.DATA_TEMPLATE_DOWNLOAD_FAILED);
		}
	}
);

export const downloadOutputReports = createAsyncThunk<void, ProcessId, { state: RootState; dispatch: AppDispatch }>(
	'globalActions/downloadOutputReports',
	async (processId, thunkAPI) => {
		let response = await downloadReports(processId);
		const fileName = `${currDateAndTime()}_${processId.jobId}_Calculation_Reports`;
		if (response.Success) {
			saveStream(response.ContentType ?? null, fileName, response.Data);
			showSuccessSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_OUTPUT_REPORTS_SUCCESS);
		} else {
			showErrorSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_OUTPUT_REPORTS_FAILED);
		}
	}
);

export const downloadAutoReviewOutputReports = createAsyncThunk<void, AutoReviewProcessId, { state: RootState; dispatch: AppDispatch }>(
	'globalActions/downloadAutoReviewOutputReports',
	async (processId, thunkAPI) => {
		let response = await downloadAutoReviewReportByCalculationId(processId.calculationId!.toString(), processId.jobId.toString());
		const fileName = `${currDateAndTime()}_${processId.jobId}_Calculation_AutoReview_Reports`;
		if (response.Success) {
			saveStream(response.ContentType ?? null, fileName, response.Data);
			showSuccessSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_AUTOREVIEW_REPORT_SUCCESS);
		} else {
			showErrorSnackbar(thunkAPI.dispatch, Messages.DOWNLOAD_AUTOREVIEW_REPORT_FAILED);
		}
	}
);

const globalActionsSlice = createSlice({
	name: 'globalActions',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addMatcher(
				isAnyOf(
					downloadTemplate.pending,
					downloadPopulatedTemplate.pending,
					downloadOutputReports.pending,
					downloadAutoReviewOutputReports.pending,
				),
				(state) => {
					state.activeDownloadRequests += 1;
				},
			)
			.addMatcher(
				isAnyOf(
					downloadTemplate.fulfilled,
					downloadTemplate.rejected,
					downloadPopulatedTemplate.fulfilled,
					downloadPopulatedTemplate.rejected,
					downloadOutputReports.fulfilled,
					downloadOutputReports.rejected,
					downloadAutoReviewOutputReports.fulfilled,
					downloadAutoReviewOutputReports.rejected,
				),
				(state) => {
					state.activeDownloadRequests = Math.max(0, state.activeDownloadRequests - 1);
				},
			);
	},
});

export default globalActionsSlice.reducer;
